/**
 * Vertical facets hide/show toggle (Vertical mode only)
 *
 * Simple + robust:
 * - Click toggles `.hidden` on the vertical facets pane immediately (no width
 *   animation, no persisted state — every page load starts from the theme
 *   setting, which Liquid renders server-side via the `hidden` class).
 * - When hidden, the results grid spans the standard centered content width;
 *   when shown, the inline value is cleared so the CSS sidebar rule applies.
 * - Button label and aria-expanded always reflect the pane state.
 */

const SELECTOR_TOGGLE = '[data-facets-vertical-toggle]';
const SELECTOR_PANE = '[data-facets-vertical-pane]';
const SELECTOR_RESULTS_GRID = '.main-collection-grid';

function getResultsGrid(btn) {
  const wrapper = btn.closest('.collection-wrapper') || document;
  return wrapper.querySelector(SELECTOR_RESULTS_GRID);
}

function setResultsFullWidth(btn, isFullWidth) {
  const grid = getResultsGrid(btn);
  if (!grid) return;

  if (isFullWidth) {
    grid.style.gridColumn = 'var(--centered)';
  } else {
    grid.style.gridColumn = '';
  }
}

function updateButtonState(btn, pane) {
  const isHidden = pane.classList.contains('hidden');
  const showLabel = btn.getAttribute('data-label-show') || 'Show Filters';
  const hideLabel = btn.getAttribute('data-label-hide') || 'Hide Filters';

  btn.setAttribute('aria-expanded', (!isHidden).toString());

  const labelEl = btn.querySelector('[data-facets-vertical-toggle-label]');
  if (labelEl) labelEl.textContent = isHidden ? showLabel : hideLabel;

  // When hidden, expand results. When shown, restore.
  setResultsFullWidth(btn, isHidden);
}

function initOne(btn) {
  // Guard against double-binding if init runs twice on the same DOM.
  if (btn.dataset.facetsToggleBound === 'true') return;
  btn.dataset.facetsToggleBound = 'true';

  // Prefer aria-controls target
  const paneId = btn.getAttribute('aria-controls');
  let pane = paneId ? document.getElementById(paneId) : null;

  // Fallback inside section
  if (!pane) {
    const sectionRoot = btn.closest('.shopify-section') || document;
    pane = sectionRoot.querySelector(SELECTOR_PANE);
  }
  if (!pane) return;

  // Ensure aria-controls is set
  const paneID = pane.getAttribute('id');
  if (paneID) btn.setAttribute('aria-controls', paneID);

  // Clean up any leftover state from the previous animated implementation.
  pane.classList.remove('facets-vertical-pane--animating');
  pane.classList.remove('facets-vertical-pane--collapsed');
  pane.style.width = '';

  // Initial state comes from the server-rendered markup (theme setting):
  // Liquid adds the `hidden` class when "Show filters" is off. No client-side
  // persistence — filters always start visible when the setting says so.
  updateButtonState(btn, pane);

  btn.addEventListener('click', (e) => {
    e.preventDefault();

    pane.classList.toggle('hidden');
    updateButtonState(btn, pane);
  });
}

function init(scope = document) {
  scope.querySelectorAll(SELECTOR_TOGGLE).forEach(initOne);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init(), { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', (e) => init(e.target));
